import { useAuth } from '@clerk/react'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Star,
} from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import { Link, useLocation } from '#router'
import { useProblemListsOptional } from '@/components/problem-lists/problem-list-context'
import { TopicIconSmall } from '@/components/problems/topic-icon-small'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import '@/styles/problem-list-sidebar.css'

const SIDEBAR_STORAGE_KEY = 'bctci-problem-list-sidebar'
const TOOLTIP_CONTENT_CLASS =
  'border-border bg-background text-foreground z-50 rounded-md border px-3 py-1.5 text-sm font-medium shadow-md'

function getDefaultCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  // Default: collapsed on mobile, expanded on desktop
  return window.innerWidth < 768
}

function getPersistedCollapsed(): boolean | null {
  if (typeof window === 'undefined') return null
  const stored = sessionStorage.getItem(SIDEBAR_STORAGE_KEY)
  if (stored === 'true') return true
  if (stored === 'false') return false
  return null
}

// ---------------------------------------------------------------------------
// SidebarSection — reusable collapsible section
// ---------------------------------------------------------------------------

function useSectionCollapsed(
  storageKey: string,
  defaultCollapsed = false,
): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return defaultCollapsed
    const stored = sessionStorage.getItem(storageKey)
    if (stored === 'true') return true
    if (stored === 'false') return false
    return defaultCollapsed
  })

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      sessionStorage.setItem(storageKey, String(next))
      return next
    })
  }, [storageKey])

  return [collapsed, toggle]
}

function SidebarSection({
  label,
  storageKey,
  defaultCollapsed = false,
  actions,
  divider = false,
  children,
}: {
  label: string
  storageKey: string
  defaultCollapsed?: boolean
  actions?: ReactNode
  divider?: boolean
  children: ReactNode
}) {
  const [collapsed, toggle] = useSectionCollapsed(storageKey, defaultCollapsed)

  return (
    <div className={cn(divider && 'mt-2 border-t border-border pt-2')}>
      <div className="hover:bg-muted mx-1 flex w-[calc(100%-0.5rem)] items-center gap-1 rounded-md px-2 py-1.5">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-1 text-left"
        >
          <ChevronDown
            className={cn(
              'text-muted-foreground h-3 w-3 shrink-0 transition-transform',
              collapsed && '-rotate-90',
            )}
          />
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            {label}
          </span>
        </button>
        {actions && (
          <span className="ml-auto flex items-center">{actions}</span>
        )}
      </div>
      {!collapsed && children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProblemListSidebar
// ---------------------------------------------------------------------------

export function ProblemListSidebar() {
  const { isSignedIn } = useAuth()
  const listCtx = useProblemListsOptional()
  const location = useLocation()
  const { data: bookChapters } = trpc.problems.getBookChapters.useQuery()
  const [collapsed, setCollapsed] = useState(() => {
    return getPersistedCollapsed() ?? getDefaultCollapsed()
  })

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      sessionStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      return next
    })
  }, [])

  const { navigate } = location

  const handleCreateList = useCallback(async () => {
    const trimmed = newListName.trim()
    if (!trimmed || !listCtx?.createList) return

    setIsCreating(true)
    setCreateError(null)

    try {
      const result = await listCtx.createList(trimmed)
      if (result) {
        setNewListName('')
        setCreateError(null)
        setShowCreateDialog(false)
        navigate(`/problems/lists/${result.id}`)
      } else {
        setCreateError('Failed to create list')
      }
    } finally {
      setIsCreating(false)
    }
  }, [newListName, listCtx, navigate])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setShowCreateDialog(open)
    if (!open) {
      setNewListName('')
      setCreateError(null)
      setIsCreating(false)
    }
  }, [])

  // Don't render sidebar if not signed in
  if (!isSignedIn) return null

  const lists = listCtx?.lists ?? []
  const customLists = lists.filter((l) => !l.isSystem)
  const activeListId = location.pathname.match(
    /^\/problems\/lists\/([^/]+)/,
  )?.[1]
  const activeChapterSlug = location.pathname.match(
    /^\/problems\/book\/([^/]+)/,
  )?.[1]
  return (
    <>
      <aside
        aria-label="Problem lists"
        data-collapsed={collapsed}
        className="problem-list-sidebar border-border relative shrink-0 border-r"
      >
        {/* Collapsed state: thin bar with expand button */}
        {collapsed ? (
          <div className="flex h-full items-start justify-center pt-3">
            <button
              type="button"
              onClick={toggleCollapsed}
              className="problem-list-sidebar-expand-btn hover:bg-muted rounded p-1"
              aria-label="Expand sidebar"
            >
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex h-full flex-col">
            {/* Sidebar header — collapse button only */}
            <div className="flex items-center justify-end px-3 pt-3 pb-1">
              <Button
                size="icon-sm"
                variant="ghost"
                className="h-6 w-6"
                title="Collapse sidebar"
                onClick={toggleCollapsed}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Scrollable sections */}
            <div className="flex-1 overflow-y-auto px-1">
              {/* Saved section */}
              <SidebarSection
                label="Saved"
                storageKey="bctci-sidebar-lists"
                actions={
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    className="h-5 w-5"
                    title="Create new list"
                    aria-label="Create new list"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                }
              >
                <Tooltip.Provider delayDuration={150}>
                  {lists.map((list) => {
                    const isActive = activeListId === list.id
                    return (
                      <Tooltip.Root key={list.id}>
                        <Tooltip.Trigger asChild>
                          <Link
                            href={`/problems/lists/${list.id}`}
                            className={cn(
                              'hover:bg-muted mx-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                              isActive && 'bg-muted font-medium',
                            )}
                          >
                            {list.isSystem ? (
                              <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
                            ) : list.sourceType === 'chapter' ? (
                              <BookOpen className="h-3.5 w-3.5 shrink-0 text-[#51B0BC]" />
                            ) : list.sourceType === 'topic' ? (
                              <TopicIconSmall className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <div className="bg-muted-foreground/20 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded text-[8px] font-bold">
                                {list.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span className="truncate">{list.name}</span>
                          </Link>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            align="center"
                            className={TOOLTIP_CONTENT_CLASS}
                          >
                            {list.name}
                            <Tooltip.Arrow className="fill-background" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    )
                  })}
                </Tooltip.Provider>

                {/* Empty state for custom lists */}
                {customLists.length === 0 && (
                  <p className="text-muted-foreground mx-1 mt-2 px-2 text-xs">
                    Click + to create your first list
                  </p>
                )}
              </SidebarSection>

              {/* Chapters section */}
              {bookChapters && bookChapters.length > 0 && (
                <SidebarSection
                  label="Chapters"
                  storageKey="bctci-sidebar-chapters"
                  defaultCollapsed
                  divider
                >
                  <Tooltip.Provider delayDuration={150}>
                    {bookChapters.map((ch) => {
                      const isActive = activeChapterSlug === ch.slug
                      return (
                        <Tooltip.Root key={ch.slug}>
                          <Tooltip.Trigger asChild>
                            <Link
                              href={`/problems/book/${ch.slug}`}
                              className={cn(
                                'hover:bg-muted mx-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                                isActive && 'bg-muted font-medium',
                              )}
                            >
                              <BookOpen className="h-3.5 w-3.5 shrink-0 text-[#51B0BC]" />
                              <span className="truncate">{ch.title}</span>
                            </Link>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              side="right"
                              align="center"
                              className={TOOLTIP_CONTENT_CLASS}
                            >
                              {ch.title}
                              <Tooltip.Arrow className="fill-background" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      )
                    })}
                  </Tooltip.Provider>
                </SidebarSection>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Create list dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create a new list</DialogTitle>
            <DialogDescription>
              Give your new list a name to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="new-list-name"
                className="mb-1 block text-sm font-medium"
              >
                Name
              </label>
              <div className="relative">
                <Input
                  id="new-list-name"
                  value={newListName}
                  onChange={(e) => {
                    if (e.target.value.length <= 40) {
                      setNewListName(e.target.value)
                      setCreateError(null)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateList()
                    }
                  }}
                  placeholder="List name"
                  maxLength={40}
                  aria-invalid={!!createError}
                  autoFocus
                />
                <span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                  {newListName.length}/40
                </span>
              </div>
              {createError && (
                <p className="text-destructive mt-1 text-xs">{createError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => handleDialogOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateList}
                disabled={!newListName.trim() || isCreating}
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
